import { ActionMenu } from "@twinkltech/curriculum-architect-component-library";
import { Button, IconActionsMoreHoriz } from "@twinkltech/web-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";

import { CustomUnitCreateModal } from "@/components/CustomUnitCreateModal/CustomUnitCreateModal";
import { ManageAdaptationNotesModal } from "@/components/ManageAdaptationNotesModal/ManageAdaptationNotesModal";
import { PreviewUnitModalContainer } from "@/components/PreviewUnitModal/PreviewUnitModal.container";
import { RemoveUnitModal } from "@/components/RemoveUnitModal/RemoveUnitModal";
import { ViewCustomUnitModal } from "@/components/ViewCustomUnitModal/ViewCustomUnitModal";

import { TableData } from "@/states/unitTableReducer/unitTableReducer";

import { paths } from "@/paths";

import { TABLE_UNIT_CARD_NS } from "../../TableUnitCard.i18n";

import { useRemoveModalUnitInfo } from "./utils/useRemoveModalUnitInfo/useRemoveModalUnitInfo";

export const CardActionMenu = ({
  cellId,
  curriculumId,
  customUnit,
  onRemoveUnit,
  onUpdateCustomUnit,
  onUpsertAdaptationNote,
  schemeId,
  subjectId,
  tableData,
}: {
  cellId: string;
  onRemoveUnit: (payload: { cellId: string }) => void;
  onUpdateCustomUnit?: (data: {
    id: string;
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
  onUpsertAdaptationNote: (payload: {
    cellId: string;
    adaptationNote: string;
  }) => void;
  curriculumId: string;
  schemeId?: string;
  subjectId: string;
  tableData: Pick<TableData, "cells" | "rows" | "columns" | "resources">;
  customUnit?: {
    id: string;
    title: string;
    description?: string;
    startOnColumn: string;
    span?: number;
    cohort: string;
  };
}) => {
  const { t } = useTranslation(TABLE_UNIT_CARD_NS);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewCustomUnitModalOpen, setIsViewCustomUnitModalOpen] =
    useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [unitIdToPreview, setUnitIdToPreview] = useState<string | null>(null);
  const [
    isManageAdaptationNotesModalOpen,
    setIsManageAdaptationNotesModalOpen,
  ] = useState(false);

  const orderedColumns = tableData.columns
    .slice()
    .sort((a, b) => a.order - b.order);

  const customUnitStartOnColumn = orderedColumns.find(
    (col) => col.id === customUnit?.startOnColumn
  )?.id;

  // when a custom unit is passed in, we need to convert the span to an end column.
  const customUnitEndOnColumn = useMemo(() => {
    // if the custom unit has a start column, step through the ordered columns to find the end column based on the span, otherwise, return the start column
    if (customUnitStartOnColumn && customUnit?.span) {
      const startIndex = orderedColumns.findIndex(
        (col) => col.id == customUnitStartOnColumn
      );
      if (startIndex !== -1) {
        const endIndex = startIndex + customUnit.span - 1;
        if (endIndex < orderedColumns.length) {
          return orderedColumns[endIndex].id;
        }
      }
    }
    return customUnit?.startOnColumn || "";
  }, [
    customUnit?.span,
    customUnit?.startOnColumn,
    customUnitStartOnColumn,
    orderedColumns,
  ]);

  const standardUnitStartColumn = schemeId
    ? tableData.columns.find(
        (col) =>
          col.id === tableData.cells.find((cell) => cell.id === cellId)?.column
      )?.id
    : null;

  const standardUnitEndColumn = schemeId
    ? (() => {
        const cell = tableData.cells.find((cell) => cell.id === cellId);
        if (cell) {
          const startIndex = orderedColumns.findIndex(
            (col) => col.id === cell.column
          );
          if (startIndex !== -1 && cell.span) {
            const endIndex = startIndex + cell.span - 1;
            if (endIndex < orderedColumns.length) {
              return orderedColumns[endIndex].id;
            }
          }
        }
        return cell?.column || null;
      })()
    : null;

  const targetCell = schemeId
    ? tableData.cells.find((cell) => cell.id === cellId)
    : null;

  const targetScheme = schemeId
    ? tableData.resources.find((res) => res.id === schemeId)
    : null;

  const canAddADaptationNotes =
    !targetCell?.isCustom && targetCell && targetScheme;

  // derive unit info for the remove modal. If we don't do it here then we have to prop drill the table data etc which is not ideal for testing and separation of concerns.
  const { removeUnitModalInfo } = useRemoveModalUnitInfo({
    cellId,
    customUnit,
    orderedColumns,
    tableData,
  });

  return (
    <>
      <ActionMenu>
        <ActionMenu.Trigger
          aria-label={t("aria.open-action-menu")}
          className="border border-transparent hover:bg-neutral-100 rounded aria-expanded:bg-neutral-100"
        >
          <IconActionsMoreHoriz />
        </ActionMenu.Trigger>
        <ActionMenu.List className="flex flex-col p-2 bg-white list-none border rounded-lg border-neutral-150 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1)] z-30">
          {() => (
            <>
              <div
                aria-labelledby="learn-more"
                className="flex flex-col gap-1"
                role="group"
              >
                {schemeId ? (
                  <>
                    <ActionMenu.StyledListItem className="border-none">
                      <Button
                        className="rounded-md font-normal"
                        variant="ghost"
                        onClick={() => {
                          // get the monotlith resource ID from the cell
                          const cellInfo = tableData.cells.find(
                            (cell) => cell.id === cellId
                          );

                          // check is BasicResource type
                          const resourceId = !cellInfo?.isCustom
                            ? cellInfo?.resourceId
                            : null;

                          if (!resourceId) {
                            console.error(
                              "No resource ID found for cell. This may have been triggered on a custom unit by accident.",
                              cellId
                            );
                            return;
                          }

                          // open the preview modal by setting the unit ID to preview
                          setUnitIdToPreview(resourceId || null);
                        }}
                      >
                        {t("preview-learning-sequence")}
                      </Button>
                    </ActionMenu.StyledListItem>
                    <ActionMenu.StyledListItem className="border-none">
                      <Button
                        as={Link}
                        className="rounded-md font-normal"
                        to={generatePath(paths.SCHEME_LEARNING_SEQUENCE_PATH, {
                          curriculum_id: curriculumId,
                          subject_id: subjectId,
                          scheme_id: schemeId,
                          cell_id: cellId ?? "generic",
                        })}
                        variant="ghost"
                      >
                        {t("view-learning-sequence")}
                      </Button>
                    </ActionMenu.StyledListItem>
                    <div className="border-b border-neutral-150 my-2" />
                    {canAddADaptationNotes && (
                      <ActionMenu.StyledListItem className="border-none">
                        <Button
                          className="rounded-md font-normal"
                          variant="ghost"
                          onClick={() =>
                            setIsManageAdaptationNotesModalOpen(true)
                          }
                        >
                          {t("manage-unit-details")}
                        </Button>
                      </ActionMenu.StyledListItem>
                    )}
                  </>
                ) : customUnit ? (
                  <ActionMenu.StyledListItem className="border-none">
                    <Button
                      className="rounded-md font-normal"
                      variant="ghost"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      {t("manage-unit-details")}
                    </Button>
                  </ActionMenu.StyledListItem>
                ) : null}
                <ActionMenu.StyledListItem className="border-none">
                  <Button
                    className="rounded-md font-normal"
                    variant="ghost"
                    onClick={() => setIsRemoveModalOpen(true)}
                  >
                    {t("remove-unit-from-plan")}
                  </Button>
                </ActionMenu.StyledListItem>
              </div>
            </>
          )}
        </ActionMenu.List>
      </ActionMenu>

      {isEditModalOpen ? (
        <CustomUnitCreateModal
          open
          customUnit={
            customUnit && customUnitStartOnColumn
              ? {
                  ...customUnit,
                  startOnColumn: customUnitStartOnColumn,
                  endOnColumn: customUnitEndOnColumn,
                }
              : undefined
          }
          tableData={tableData}
          title="Edit Custom Unit"
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={(data) => {
            const dataHasIdTypeguard = (obj: object): obj is { id: string } => {
              return "id" in obj && typeof obj.id === "string";
            };

            if (dataHasIdTypeguard(data)) {
              onUpdateCustomUnit?.(data);
            } else {
              console.error("No custom unit ID provided");
            }
          }}
        />
      ) : null}

      {isRemoveModalOpen ? (
        <RemoveUnitModal
          cellId={cellId}
          unit={removeUnitModalInfo}
          onClose={() => setIsRemoveModalOpen(false)}
          onSubmit={onRemoveUnit}
        />
      ) : null}

      {isViewCustomUnitModalOpen && customUnit ? (
        <ViewCustomUnitModal
          open
          customUnit={{
            title: customUnit.title,
            description: customUnit.description,
            cohort:
              tableData.rows
                .slice()
                .find((row) => row.id === customUnit?.cohort)?.title || "",
            startOnColumn:
              orderedColumns.find((col) => col.id === customUnit?.startOnColumn)
                ?.title || "",
            endOnColumn:
              orderedColumns.find((col) => col.id === customUnitEndOnColumn)
                ?.title || "",
          }}
          onClose={() => setIsViewCustomUnitModalOpen(false)}
        />
      ) : null}

      {canAddADaptationNotes &&
        targetScheme &&
        isManageAdaptationNotesModalOpen && (
          <ManageAdaptationNotesModal
            open
            cell={targetCell}
            cohort={
              tableData.rows.find((row) => row.id === targetCell?.row)?.title ||
              ""
            }
            scheme={targetScheme}
            termEnd={
              orderedColumns.find((col) => col.id === standardUnitEndColumn)
                ?.title || ""
            }
            termStart={
              orderedColumns.find((col) => col.id === standardUnitStartColumn)
                ?.title || ""
            }
            onClose={() => setIsManageAdaptationNotesModalOpen(false)}
            onSubmit={onUpsertAdaptationNote}
          />
        )}
      {unitIdToPreview !== null && (
        <PreviewUnitModalContainer
          open={unitIdToPreview !== null}
          unitId={unitIdToPreview ?? ""}
          onClose={() => setUnitIdToPreview(null)}
        />
      )}
    </>
  );
};
